import {
  UserUpdateMetadataType,
  UserUpdateRequestType,
  UserUpdateResponseType,
} from "@sellar-io/api-v2/types";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useFirebaseUser } from "../../../useFirebaseUser.shared";
import { useSellarCurrentBusiness } from "../../../useSellarCurrentBusiness.shared";
import {
  apiV2AssertResponse,
  apiV2Request,
  generateSellarMetaData,
} from "../apiV2Request";
import { ServerStateKeys } from "../ServerStateKeys";

export const useUserUpdate = (userId: number) => {
  const queryClient = useQueryClient();
  const { business } = useSellarCurrentBusiness();
  const { user } = useFirebaseUser();

  return useMutation({
    // Profile completion during signup must not wait for a browser online event.
    networkMode: "always",
    retry: false,
    mutationFn: (request: {
      data: UserUpdateRequestType;
      sellarMetadata?: UserUpdateMetadataType;
    }) =>
      apiV2Request({
        timeout: 15000,
        user,
        business,
        method: "put",
        path: `users/${userId}`,
        data: request.data,
        sellarMetadata: request.sellarMetadata
          ? generateSellarMetaData(request.sellarMetadata)
          : undefined,
      }).then(
        (response) =>
          apiV2AssertResponse(response).data as UserUpdateResponseType,
      ),

    onSettled: (data) => {
      if (data) {
        queryClient.invalidateQueries({
          queryKey: [ServerStateKeys.SellarUser],
          exact: true,
        });
        queryClient.invalidateQueries({
          queryKey: [
            user?.uid,
            business?.id,
            ServerStateKeys.SellarUser,
            userId,
          ],
        });
        queryClient.invalidateQueries({
          queryKey: [user?.uid, business?.id, ServerStateKeys.SellarUser],
        });
        queryClient.invalidateQueries({
          queryKey: [user?.uid, business?.id, ServerStateKeys.Orders],
        });
        queryClient.invalidateQueries({
          queryKey: [business?.id, ServerStateKeys.OrderComments],
        });
        queryClient.invalidateQueries({
          queryKey: [user?.uid, business?.id, ServerStateKeys.MessageInbox],
        });
        queryClient.invalidateQueries({
          queryKey: [
            user?.uid,
            business?.id,
            ServerStateKeys.RelationshipComments,
          ],
        });
        queryClient.invalidateQueries({
          queryKey: [
            user?.uid,
            business?.id,
            ServerStateKeys.RelationshipComments,
          ],
        });
      }
    },
  });
};
